'use client'

import { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import VideoPlayer from '@/components/VideoPlayer'
import LockScreenModal from '@/components/LockScreenModal'
import dynamic from 'next/dynamic'
import PainMatrixXPImproved from '@/components/PainMatrixXPImproved'
import OptimizedImage from '@/components/OptimizedImage'
import OptimizedVideo from '@/components/OptimizedVideo'
import { SpeedInsights } from "@vercel/speed-insights/next"

const TERMINAL_LINES = [
  '正在初始化安全连接...',
  'ESTABLISHING ENCRYPTED CHANNEL...',
  '验证节点完整性...',
  'LOADING MARKET DATA...',
  '访问已授权 // ACCESS GRANTED.',
]

type ScreensaverImage = {
  id: number;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  imageNumber: number;
}

const generateRandomPosition = () => {
  return {
    x: Math.random() * 100, // 0% to 100% of screen width
    y: Math.random() * 100, // 0% to 100% of screen height
    rotation: Math.random() * 360,
    scale: 0.7 + Math.random() * 0.4,
  }
}

const generateScreensaverImages = () => {
  return Array.from({ length: 45 }, (_, i) => ({
    id: i,
    ...generateRandomPosition(),
    delay: Math.random() * 15, // Random delay within 15 seconds
    zIndex: Math.floor(Math.random() * 10) // Random layering
  }))
}

// Update the generateImageUrl function to skip ss_41.png
const generateImageUrl = (index: number) => {
  // Adjust index to skip 41
  const adjustedIndex = index >= 41 ? index + 1 : index;
  return `https://cdn.shopify.com/s/files/1/0675/1328/6842/files/ss_${adjustedIndex}.png?v=1740373976`;
}

const RotatingCube = dynamic(() => import('@/components/RotatingCube'), { ssr: false });
const PainMatrixInteractive = dynamic(() => import('@/components/PainMatrixInteractive'), { ssr: false });

export default function HomePage() {
  const [visibleLines, setVisibleLines] = useState<number>(0)
  const [showMenu, setShowMenu] = useState(false)
  const [showNewsletterModal, setShowNewsletterModal] = useState(false)
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [expandedItem, setExpandedItem] = useState<string | null>(null)
  const [isLocked, setIsLocked] = useState(true)
  const terminalRef = useRef<HTMLDivElement>(null)
  const [hasAnimationStarted, setHasAnimationStarted] = useState(false)
  const terminalText = useMemo(() => TERMINAL_LINES, [])
  const [isScreensaverActive, setIsScreensaverActive] = useState(false)
  const [currentImages, setCurrentImages] = useState<ScreensaverImage[]>([])
  const [lastActivity, setLastActivity] = useState(Date.now())
  const [shouldShowLockScreen, setShouldShowLockScreen] = useState(true)
  const [isScreensaver, setIsScreensaver] = useState(false)
  const [images, setImages] = useState<ScreensaverImage[]>([])
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [showMethodModal, setShowMethodModal] = useState(false)
  const [showHeroImageModal, setShowHeroImageModal] = useState(false)

  // Move SCREENSAVER_IMAGES inside the component
  const SCREENSAVER_IMAGES = useMemo(() => {
    return Array.from({length: 46}, (_, i) => generateImageUrl(i + 1));
  }, []);

  // Add newsletter delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNewsletterModal(true)
    }, 15000) // 15 seconds delay

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Check if user has already unlocked the page in this session
    const hasUnlocked = sessionStorage.getItem('hasUnlocked');
    if (hasUnlocked === 'true') {
      setShouldShowLockScreen(false);
      setIsLocked(false);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Subscription failed')
      }

      // Show success message
      setError('')
      // Use a more cyberpunk-themed success message
      alert('[SUCCESS] You are subscribed. We may or may not share your data with our marketing partners (Dongfeng Media Network).')
      
      // Clear the form
      setEmail('')
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleLines(prev => {
        if (prev < TERMINAL_LINES.length) {
          return prev + 1
        }
        clearInterval(timer)
        setShowMenu(true)
        return prev
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const handleActivity = () => {
      setLastActivity(Date.now())
      if (isScreensaverActive) {
        setIsScreensaverActive(false)
        setCurrentImages([])
      }
    }

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart']
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity)
    })

    const checkInactivity = setInterval(() => {
      if (Date.now() - lastActivity > 23000) { // Changed from 16000 to 23000 (23 seconds)
        setIsScreensaverActive(true)
      }
    }, 1000)

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity)
      })
      clearInterval(checkInactivity)
    }
  }, [lastActivity, isScreensaverActive])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const addImage = () => {
      if (isScreensaverActive && currentImages.length < 46) {
        setCurrentImages(prev => [
          ...prev,
          {
            id: prev.length,
            ...generateRandomPosition(),
            imageNumber: prev.length + 1
          }
        ]);
        
        // Longer delays between stickers (2-4 seconds)
        const nextDelay = 2000 + Math.random() * 2000;
        timeoutId = setTimeout(addImage, nextDelay);
      }
    };

    if (isScreensaverActive && currentImages.length < 46) {
      timeoutId = setTimeout(addImage, 2000); // Initial delay of 2 seconds
    }

    return () => clearTimeout(timeoutId);
  }, [isScreensaverActive, currentImages.length]);

  const handleUnlock = () => {
    setIsLocked(false);
    // Store unlock state in session storage
    sessionStorage.setItem('hasUnlocked', 'true');
  };

  return (
    <div
      className="min-h-screen overflow-x-hidden relative"
      style={{
        color: '#fff',
        overflowY: showMenu ? 'auto' : 'visible',
        background: 'linear-gradient(180deg, #1a0033 0%, #0d1117 25%, #1e3a5f 50%, #4a7ba7 75%, #ffffff 100%)',
        backgroundAttachment: 'fixed',
        backgroundSize: 'cover'
      }}
    >
      {isScreensaverActive && (
        <>
          {/* Light blur overlay without dimming */}
          <div className="fixed inset-0 backdrop-blur-sm z-40 transition-all duration-1000" />
          
          <div className="fixed inset-0 z-50 pointer-events-none">
            {currentImages.map((img) => (
              <Image
                key={img.id}
                src={generateImageUrl(img.imageNumber)}
                alt="Screensaver"
                width={128}
                height={128}
                style={{
                  position: 'absolute',
                  left: `${img.x}%`,
                  top: `${img.y}%`,
                  transform: `rotate(${img.rotation}deg) scale(${img.scale})`,
                  marginLeft: '-64px',
                  marginTop: '-64px',
                }}
              />
            ))}
          </div>
        </>
      )}

      {shouldShowLockScreen && (
        <LockScreenModal 
          isLocked={isLocked} 
          onUnlock={handleUnlock}
        />
      )}

      {/* Method Modal */}
      {showMethodModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80" onClick={() => setShowMethodModal(false)}>
          <div 
            className="method-modal-container relative max-w-4xl w-full mx-4" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <div className="method-modal-glow absolute -inset-1 bg-gradient-to-r from-red-500 via-yellow-500 to-red-500 rounded-lg blur-lg opacity-75"></div>
              <div className="relative bg-black rounded-lg p-1">
                <div className="relative overflow-hidden rounded-lg">
                  <div className="method-modal-image">
                    <Image
                      src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/method.png?v=1741329867"
                      alt="The Method"
                      width={1200}
                      height={800}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  
                  {/* Animated particles */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {[...Array(20)].map((_, i) => (
                      <div 
                        key={i}
                        className="absolute rounded-full bg-white"
                        style={{
                          width: `${Math.random() * 4 + 1}px`,
                          height: `${Math.random() * 4 + 1}px`,
                          top: `${Math.random() * 100}%`,
                          left: `${Math.random() * 100}%`,
                          opacity: Math.random() * 0.5 + 0.2,
                          animation: `float ${Math.random() * 6 + 3}s ease-in-out infinite`,
                          animationDelay: `${Math.random() * 5}s`
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Overlay text */}
                  
                </div>
                <button 
                  className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors"
                  onClick={() => setShowMethodModal(false)}
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx global>{`
        :root {
          --masonry-end: 2400px;
        }

        @keyframes shine {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes pulse {
          0% { opacity: 0.3; }
          50% { opacity: 0.7; }
          100% { opacity: 0.3; }
        }
        @keyframes scroll {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes tiltRotate {
          0%, 100% { transform: rotate(-2deg); }
          50% { transform: rotate(2deg); }
        }
        @keyframes oldSchoolBounce {
          0%, 100% { transform: translateY(0); }
          25% { transform: translateY(-2px); }
          50% { transform: translateY(0); }
          75% { transform: translateY(-2px); }
        }
        @keyframes eccentric-shake {
          0%, 100% { 
            transform: translate(0, 0) rotate(0deg); 
          }
          10% { 
            transform: translate(-5px, 3px) rotate(-2deg); 
          }
          20% { 
            transform: translate(7px, -4px) rotate(3deg); 
          }
          30% { 
            transform: translate(-9px, 2px) rotate(-3deg); 
          }
          40% { 
            transform: translate(4px, -6px) rotate(1deg); 
          }
          50% { 
            transform: translate(-3px, 5px) rotate(-1deg); 
          }
          60% { 
            transform: translate(8px, 3px) rotate(2deg); 
          }
          70% { 
            transform: translate(-6px, -4px) rotate(-2deg); 
          }
          80% { 
            transform: translate(5px, 2px) rotate(1deg); 
          }
          90% { 
            transform: translate(-4px, -3px) rotate(3deg); 
          }
        }
        @keyframes stiff-shake {
          0%, 100% { 
            transform: translate(0, 0); 
          }
          25% { 
            transform: translate(-2px, 1px); 
          }
          50% { 
            transform: translate(2px, -1px); 
          }
          75% { 
            transform: translate(-1px, 1px); 
          }
        }
        @keyframes rotate-3d {
          from {
            transform: rotateY(0deg);
          }
          to {
            transform: rotateY(360deg);
          }
        }
        .animate-eccentric-shake {
          animation: eccentric-shake 0.5s cubic-bezier(.36,.07,.19,.97) infinite;
          transform-origin: center;
        }
        .animate-stiff-shake {
          animation: stiff-shake 0.15s steps(2) infinite;
        }
        .animate-3d-rotate {
          animation: rotate-3d 8s linear infinite;
          transform-style: preserve-3d;
          backface-visibility: visible;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.3) rotate(0deg);
          }
          to {
            opacity: 1;
            transform: scale(1) rotate(${Math.random() * 360}deg);
          }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(-1deg); }
          50% { transform: translateY(-15px) rotate(2deg); }
        }
        @keyframes shine-slide {
          0% { transform: translateX(-100%) skewX(-25deg); }
          20% { transform: translateX(100%) skewX(-25deg); }
          100% { transform: translateX(100%) skewX(-25deg); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Terminal-specific animations */
        @keyframes terminalFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Terminal line specific styling */
        .terminal-line {
          opacity: 0;
          animation: terminalFadeIn 0.5s ease-out forwards;
          transform: none !important; /* Ensure no transform is applied */
        }

        /* Button animations */
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(-1deg); }
          50% { transform: translateY(-15px) rotate(2deg); }
        }

        @keyframes shine-slide {
          0% { transform: translateX(-100%) skewX(-25deg); }
          20% { transform: translateX(100%) skewX(-25deg); }
          100% { transform: translateX(100%) skewX(-25deg); }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Force console font on masonry headlines */
        .masonry-headline {
          font-family: "Lucida Console", Monaco, monospace !important;
          font-weight: normal !important;
          letter-spacing: -0.02em !important;
        }

        /* Paradise image specific styling */
        @keyframes rotate-3d {
          from { transform: rotateY(0deg); }
          to { transform: rotateY(360deg); }
        }

        .paradise-rotate {
          animation: rotate-3d 8s linear infinite;
          transform-style: preserve-3d;
          backface-visibility: visible;
        }
      `}</style>

      <div className="absolute inset-0">
        <div className="hero-image-container max-w-full">
          <Image 
            src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/hero_63ec1957-21e1-47d6-9e88-8f9ce5abbd88.gif?v=1741308087" 
            alt="Hero Image" 
            className="hero-image"
            fill
            unoptimized
            priority
            quality={100}
            sizes="100vw"
          />
        </div>

        {/* IE Warning */}
       {/* <div className="w-full bg-[#FFFBE6] border border-[#8B4513] text-[#8B4513] p-3 text-center text-sm font-mono">
          Best viewed in Internet Explorer 6.0 or Netscape Navigator. Click here to download Flash Player.
        </div>*/}

        {/* Federal Notice */}
        <div className="w-full bg-[#222] border-y border-[#333] py-2 overflow-hidden">
          <div className="status-ticker-container">
            <div className="status-ticker-content">
              {/* Repeat the notice twice for seamless loop */}
              {[1, 2].map((item) => (
                <div key={item} className="status-item">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
                  <span className="text-[#ff9800] text-xs">
                    [!] SHIPPING QUEUE: <span className="text-yellow-400">IN PACKING</span> [!] D_ID: JAN15-05
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="header-panel bg-[#1e1e1e]">
          {/* Ad Container */}
          <div className="w-full mx-auto flex flex-col gap-4 px-4">
            {/* Shop Banner */}
            <Link href="/shop" className="ad-banner-container w-full h-[120px] relative overflow-hidden group">
              <img 
                src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/df_ie_banner.jpg?v=1738454347"
                alt="Shop Latest Collection"
                className="absolute inset-0 w-full h-full object-contain object-center transform group-hover:scale-105 transition-transform duration-500"
              />
            </Link>
            
            {/* Intel Banner */}
            <Link href="/intel" className="ad-banner-container w-full h-[120px] relative overflow-hidden group">
              <img 
                src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/ad1.gif?v=1738454347"
                alt="AD"
                className="absolute inset-0 w-full h-full object-contain object-center transform group-hover:scale-105 transition-transform duration-500"
              />
            </Link>
          </div>

          <img 
            src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/DF_Logo_2024_white.png?v=1738029588" 
            alt="Shareholder Panel Logo" 
            className="header-logo -my-2 -ml-10"
          />
          <form onSubmit={handleLogin} className="login-container">
            <div className="login-group">
              <span className="login-label">Newsletter Access</span>
              <input 
                type="email" 
                placeholder="Use email, get insider trading news"
                className="login-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                title="Please enter a valid email address"
              />
            </div>
            <div className="auth-buttons">
              <button 
                type="submit" 
                className="auth-button"
                disabled={loading}
              >
                <img 
                  src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/user-icon.png" 
                  alt="" 
                  className="w-4 h-4 inline-block mr-2 align-text-bottom"
                />
                {loading ? '加入中... // CONNECTING...' : 'ACCESS NEWSLETTER'}
              </button>
              <a 
                href="https://t.me/df5hq"
                target="_blank"
                rel="noopener noreferrer"
                className="telegram-button shine-effect"
                style={{ opacity: 1 }}
              >
                <div className="shine-overlay" style={{ opacity: 0.8 }}></div>
                <div className="relative z-10">
                  <img 
                    src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/icons8-telegram-94.png?v=1740261997" 
                    alt="" 
                    className="w-5 h-5 inline-block mr-2 align-text-bottom"
                  />
                  REQUEST TELEGRAM ACCESS ⚡️
                </div>
              </a>
            </div>
            {error && (
              <div className="error-message text-red-500 text-sm mt-2 font-mono bg-red-500/10 p-2 border border-red-500/20">
                [ERROR] {error}
              </div>
            )}
          </form>
        </div>

        

        {/* Terminal Section - Now separate from header panel */}
        <div className="border-t border-[#333]">
          <div className="px-4">
            <div className="terminal max-w-2xl mx-auto">
              <div className="terminal-header">
                <span className="text-xs text-gray-500">C-Suite Terminal / Build 0.1.21</span>
                <div className="terminal-status">
                  <div className="terminal-dot bg-green-400"></div>
                  <span className="text-xs text-gray-500">CONNECTED</span>
                </div>
              </div>

              {TERMINAL_LINES.slice(0, visibleLines).map((line, index) => (
                <div 
                  key={index}
                  className="terminal-line"
                  style={{ 
                    animationDelay: `${index * 0.2}s`,
                    transform: 'none'  // Explicitly set no transform
                  }}
                >
                  <span className="terminal-prompt">{'>'}</span>
                  <span>{line}</span>
                </div>
              ))}

              {showMenu && (
                <div className="terminal-menu">
                  <Link href="/shop" className="terminal-option">
                    <span className="text-gray-500 mr-2">[1]</span>
                    <span>访问市场 // ACCESS MARKETPLACE</span>
                  </Link>
                  {/* Intel menu option - always locked in production */}
                  <div className="terminal-option opacity-50 cursor-not-allowed">
                    <span className="text-gray-500 mr-2">[2]</span>
                    <span>查看情报 // VIEW INTELLIGENCE [LOCKED]</span>
                  </div>
                  <Link href="/fulfillment" className="terminal-option">
                    <span className="text-gray-500 mr-2">[3]</span>
                    <span>订单队列 // ORDER QUEUE</span>
                  </Link>
                  <div className="terminal-option opacity-50 cursor-not-allowed">
                    <span className="text-gray-500 mr-2">[4]</span>
                    <span>股东登记 // SHAREHOLDER REGISTRY [LOCKED]</span>
                  </div>
                  <div className="terminal-option opacity-50 cursor-not-allowed">
                    <span className="text-gray-500 mr-2">[5]</span>
                    <span>内部备忘录 // INTERNAL MEMOS [LOCKED]</span>
                  </div>
                </div>
              )}

              {/* Industrial Classifieds Section */}
              <div className="border-t border-[#333]">
                <div className="bg-[#111] rounded-sm overflow-hidden">
                  {/* Header */}
                  <div className="bg-[#222] px-4 py-2 flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-[#ff9800] animate-pulse"></div>
                    <div className="flex-1 overflow-hidden">
                      <div className="overflow-x-hidden whitespace-nowrap animate-[scroll_20s_linear_infinite]">
                        <span className="inline-block font-['Courier_New'] text-[#ff9800] text-xs">ALIBABA API // THANK YOU FOR YOUR BUSINESS // INQUIRIES: CEO@DONGFENGFIVE.COM // SHOP WITH OUR PARTNERS // PRICES SUBJECT TO MARKET CHANGES
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Listings Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-[1px] bg-[#222]">
                    {/* Listing 1 */}
                    <div className="bg-[#111] relative group transition-all duration-300">
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedItem(expandedItem === 'press' ? null : 'press')}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#ff9800]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative">
                          <div className="flex items-start justify-between mb-2">
                            <div className="font-['Courier_New']">
                              <div className="text-[#ff9800] text-xs mb-1">LOT #A-117-B</div>
                              <div className="text-white text-sm">MOBILE CREMATORIUM (ECO-FRIENDLY)</div>
                            </div>
                            <div className="px-2 py-1 bg-[#1a1a1a] rounded">
                              <span className="text-[#ff9800] text-xs font-['Courier_New']">¥2.4M</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">ORIGIN:</span>
                              <span className="text-gray-300 font-['Courier_New']">Linyi Heaven Funeral Supplies Co., Ltd.</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">STATUS:</span>
                              <span className="text-yellow-500 font-['Courier_New']">REFURBISHED</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">STOCK:</span>
                              <span className="text-green-500 font-['Courier_New']">2 UNIT(s)</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded Content */}
                      <div 
                        className={`overflow-hidden transition-all duration-500 ${
                          expandedItem === 'press' ? 'max-h-[500px]' : 'max-h-0'
                        }`}
                      >
                        <div className="p-4 border-t border-[#1d2839] bg-[#0d1117]">
                          <div className="marketplace-image-container">
                            <Image
                              src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/crematory.avif?v=1741308388"
                              alt="Mobile Crematory"
                              width={640}
                              height={360}
                              className="marketplace-image"
                            />
                          </div>
                          {/* Placeholder for spacing */}
                          <div className="h-12 mt-4"></div>
                        </div>
                      </div>
                      
                      {/* Absolutely positioned button that appears when expanded */}
                      {expandedItem === 'press' && (
                        <div className="marketplace-button-container">
                          <button 
                            className="w-full bg-[#1a2332] hover:bg-[#243142] text-[#00ff9d] py-2 px-4 rounded text-sm font-['Courier_New'] border border-[#1d2839] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open("https://www.alibaba.com/product-detail/Factory-Sale-E-Friendly-Mobile-Crematorium_1600101187467.html?spm=a2700.galleryofferlist.normal_offer.d_title.79d213a0LGklW7", "_blank", "noopener,noreferrer");
                            }}
                          >
                            VIEW ON ALIBABA ⟶
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Listing 2 */}
                    <div className="bg-[#111] relative group transition-all duration-300">
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedItem(expandedItem === 'reactor' ? null : 'reactor')}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#ff9800]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative">
                          <div className="flex items-start justify-between mb-2">
                            <div className="font-['Courier_New']">
                              <div className="text-[#ff9800] text-xs mb-1">LOT #X-227-C</div>
                              <div className="text-white text-sm">FIMT 32 PORT GSM MODEM POOL</div>
                            </div>
                            <div className="px-2 py-1 bg-[#1a1a1a] rounded">
                              <span className="text-[#ff9800] text-xs font-['Courier_New']">$1,212.22 USD</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">ORIGIN:</span>
                              <span className="text-gray-300 font-['Courier_New']">
                              Beijing Jingda Shengye Technology Co., Ltd.</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">STATUS:</span>
                              <span className="text-blue-400 font-['Courier_New']">TESTED</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">STOCK:</span>
                              <span className="text-green-500 font-['Courier_New']">3 UNIT(s)</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      <div 
                        className={`overflow-hidden transition-all duration-500 ${
                          expandedItem === 'reactor' ? 'max-h-[500px]' : 'max-h-0'
                        }`}
                      >
                        <div className="p-4 border-t border-[#1d2839] bg-[#0d1117]">
                          <div className="marketplace-image-container">
                            <Image
                              src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/rBVaWVwQj7KALAJxAAHXSEP_etI338.webp?v=1741328271"
                              alt="GSM"
                              width={640}
                              height={360}
                              className="marketplace-image"
                            />
                          </div>
                          {/* Placeholder for spacing */}
                          <div className="h-12 mt-4"></div>
                        </div>
                      </div>
                      
                      {/* Absolutely positioned button that appears when expanded */}
                      {expandedItem === 'reactor' && (
                        <div className="marketplace-button-container">
                          <button 
                            className="w-full bg-[#1a2332] hover:bg-[#243142] text-[#00ff9d] py-2 px-4 rounded text-sm font-['Courier_New'] border border-[#1d2839] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open("https://www.dhgate.com/product/fimt-32-port-modem-pool-wavecom-support-sms/437813857.html?f=bm%7CGMC%7Cpla%7C21041233832%7C%7C437813857%7C%7C104004003%7CUS%7Cgoodgo%7Cc%7C2%7CCjwKCAiArKW-BhAzEiwAZhWsIEd--YP6Wd_3A-ouNKlvSsbJVHCTISYoLneUeDdKkDOqzi7Ou2E13RoCUuUQAvD_BwE%7C&utm_source=pla&utm_medium=GMC&utm_campaign=goodgo&utm_term=437813857&linkPage=channelSale&f=bm%7CGMC%7Cpla%7C21041233832%7CUS%7C%7C%7C%7C%7C%7C%7CCjwKCAiArKW-BhAzEiwAZhWsIEd--YP6Wd_3A-ouNKlvSsbJVHCTISYoLneUeDdKkDOqzi7Ou2E13RoCUuUQAvD_BwE%7C%7C&gad_source=1&gclid=CjwKCAiArKW-BhAzEiwAZhWsIEd--YP6Wd_3A-ouNKlvSsbJVHCTISYoLneUeDdKkDOqzi7Ou2E13RoCUuUQAvD_BwE&skuId=533376475547181056", "_blank", "noopener,noreferrer");
                            }}
                          >
                            VIEW ON DHGATE ⟶
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Listing 3 */}
                    <div className="bg-[#111] relative group transition-all duration-300">
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedItem(expandedItem === 'cnc' ? null : 'cnc')}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#ff9800]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative">
                          <div className="flex items-start justify-between mb-2">
                            <div className="font-['Courier_New']">
                              <div className="text-[#ff9800] text-xs mb-1">LOT #M-445-A</div>
                              <div className="text-white text-sm">CRUDE OIL DISTILLATION COLUMN</div>
                            </div>
                            <div className="px-2 py-1 bg-[#1a1a1a] rounded">
                              <span className="text-[#ff9800] text-xs font-['Courier_New']">$33,200.00 USD</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">ORIGIN:</span>
                              <span className="text-gray-300 font-['Courier_New']">Shanghai Yuanhuai Chemical Technology Co., Ltd.</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">STATUS:</span>
                              <span className="text-green-400 font-['Courier_New']">NEW</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">STOCK:</span>
                              <span className="text-red-500 font-['Courier_New']">3+ UNIT(s)</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      <div 
                        className={`overflow-hidden transition-all duration-500 ${
                          expandedItem === 'cnc' ? 'max-h-[500px]' : 'max-h-0'
                        }`}
                      >
                        <div className="p-4 border-t border-[#1d2839] bg-[#0d1117]">
                          <div className="marketplace-image-container">
                            <Image
                              src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/Ha45cfbfad47c400aa605971f27ac69be8.png_720x720q50.avif?v=1741329265"
                              alt="Refinery"
                              width={640}
                              height={360}
                              className="marketplace-image"
                            />
                          </div>
                          {/* Placeholder for spacing */}
                          <div className="h-12 mt-4"></div>
                        </div>
                      </div>
                      
                      {/* Absolutely positioned button that appears when expanded */}
                      {expandedItem === 'cnc' && (
                        <div className="marketplace-button-container">
                          <button 
                            className="w-full bg-[#1a2332] hover:bg-[#243142] text-[#00ff9d] py-2 px-4 rounded text-sm font-['Courier_New'] border border-[#1d2839] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open("https://www.alibaba.com/product-detail/Crude-Oil-Distillation-Column-Continuous-Column_1601206566711.html?spm=a2700.galleryofferlist.p_offer.d_title.131f13a0xmLrwD&s=p", "_blank", "noopener,noreferrer");
                            }}
                          >
                            VIEW ON ALIBABA ⟶
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Listing 4 */}
                    <div className="bg-[#111] relative group transition-all duration-300">
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedItem(expandedItem === 'furnace' ? null : 'furnace')}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#ff9800]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="relative">
                          <div className="flex items-start justify-between mb-2">
                            <div className="font-['Courier_New']">
                              <div className="text-[#ff9800] text-xs mb-1">LOT #K-992-D</div>
                              <div className="text-white text-sm">MUSCLE GAIN POWER CHEWS</div>
                            </div>
                            <div className="px-2 py-1 bg-[#1a1a1a] rounded">
                              <span className="text-[#ff9800] text-xs font-['Courier_New']">$7.00 USD</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">ORIGIN:</span>
                              <span className="text-gray-300 font-['Courier_New']">Guangzhou Hanyun Biotechnology Co., Ltd</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">STATUS:</span>
                              <span className="text-purple-400 font-['Courier_New']">NEW</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <span className="text-gray-500">STOCK:</span>
                              <span className="text-green-500 font-['Courier_New']">1000+ UNIT(s)</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      <div 
                        className={`overflow-hidden transition-all duration-500 ${
                          expandedItem === 'furnace' ? 'max-h-[500px]' : 'max-h-0'
                        }`}
                      >
                        <div className="p-4 border-t border-[#1d2839] bg-[#0d1117]">
                          <div className="marketplace-image-container">
                            <Image
                              src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/Hdc67e66c7405475a8357f5a0455d78b1E.jpg_720x720q50.avif?v=1741329024"
                              alt="Muscle Gain Power Chews"
                              width={640}
                              height={360}
                              className="marketplace-image"
                            />
                          </div>
                          {/* Placeholder for spacing */}
                          <div className="h-12 mt-4"></div>
                        </div>
                      </div>
                      
                      {/* Absolutely positioned button that appears when expanded */}
                      {expandedItem === 'furnace' && (
                        <div className="marketplace-button-container">
                          <button 
                            className="w-full bg-[#1a2332] hover:bg-[#243142] text-[#00ff9d] py-2 px-4 rounded text-sm font-['Courier_New'] border border-[#1d2839] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open("https://www.alibaba.com/product-detail/Muscle-Gain-Power-Chews-High-Protein_1600827187282.html?spm=a2700.galleryofferlist.p_offer.d_image.80f413a0I6mOcp&s=p", "_blank", "noopener,noreferrer");
                            }}
                          >
                            VIEW ON ALIBABA ⟶
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-[#0a0a0a] px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                        <span className="text-[10px] text-gray-500 font-['Courier_New']">CONNECTION ACTIVE</span>
                      </div>
                      
                    </div>
                    <div className="text-[10px] text-gray-500 font-['Courier_New']">
                      LAST SYNC: 22.02.2025 21:44:13
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        

        {/* Masonry Grid Menu */}
        <div className="w-full max-w-[1400px] mx-auto bg-[#111] my-12">
          <div className="grid grid-cols-2 gap-px">
            {/* Column 1 - Two rows */}
            <div className="grid gap-px">
              <div className="block masonry-item-locked">
                <div className="relative aspect-square bg-[#1a1a1a] overflow-hidden group">
                  <Image
                    src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/inter_5.png?v=1740356825"
                    alt="RAP"
                    fill
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80"></div>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 bg-[#33ff33]/5"></div>
                  <div className="absolute bottom-0 left-0 p-3 sm:p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="text-[#33ff33]/50 text-xs sm:text-sm mb-1 sm:mb-2 font-mono">ASSET RECOVERY PROGRAM</div>
                    <h3 className="masonry-headline text-white text-base sm:text-2xl mb-1 sm:mb-2">Interpol Joint Operation: D.O.N.G</h3>
                  </div>
                </div>
              </div>
              
              <div className="block masonry-item-locked">
                <div className="relative aspect-square bg-[#1a1a1a] overflow-hidden group">
                  <Image
                    src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/DF5FACTORY.png?v=1740336099"
                    alt="Miracle Powder"
                    fill
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80"></div>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 bg-[#33ff33]/5"></div>
                  <div className="absolute bottom-0 left-0 p-3 sm:p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="text-[#33ff33]/50 text-xs sm:text-sm mb-1 sm:mb-2 font-mono">PRODUCT PORTFOLIO</div>
                    <h3 className="masonry-headline text-white text-base sm:text-2xl mb-1 sm:mb-2">Miracle Powder</h3>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2 - One tall item */}
            <div className="h-full">
              <div className="block h-full masonry-item-locked">
                <div className="relative h-full bg-[#1a1a1a] overflow-hidden group">
                  <Image
                    src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/heroimagetest.webp?v=1738033501"
                    alt="The Grid"
                    fill
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80"></div>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 bg-[#33ff33]/5"></div>
                  <div className="absolute bottom-0 left-0 p-3 sm:p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="text-[#33ff33]/50 text-xs sm:text-sm mb-1 sm:mb-2 font-mono">03 // COINTEL</div>
                    <h3 className="masonry-headline text-white text-base sm:text-2xl mb-1 sm:mb-2">Iran Toshiba Dossier</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        
        {/* Bottom Image */}
        <div className="w-full">
          <Image
            src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/hufsocks_window.png?v=1740346402"
            alt="Bottom Image"
            width={1920}
            height={1080}
            className="w-full"
            priority
          />
        </div>

        {/* Bottom Image with Stiff Shake */}
        <div className="w-[293px] h-[126px] mx-auto relative mb-8 mt-8 [perspective:1000px]">
          <Image
            src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/doyouwanttomovetoparadise.jpg?v=1740372560"
            alt="Bottom Image"
            width={293}
            height={126}
            className="w-full h-full paradise-rotate"
            priority
          />
        </div>

        {/* Secondary Hero Image - 4:5 Aspect Ratio */}
        <div className="flex flex-wrap justify-center">
          {/* First Hero Image */}
          <div className="w-full md:w-1/2 lg:w-1/3">
            <div className="hero-image-container bg-gray-900 animate-[tiltRotate_4s_ease-in-out_infinite]">
              <Image 
                src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/revelation_hero.webp?v=1740198682"
                alt="Secondary Hero Image"
                className="hero-image cursor-pointer"
                fill
                priority
                quality={100}
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                onClick={() => setShowHeroImageModal(true)}
              />
            </div>
          </div>

          {/* Image Popup */}
          {showHeroImageModal && (
            <div 
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
              onClick={() => setShowHeroImageModal(false)}
            >
              <Image
                src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/learnmore.webp?v=1740198682"
                alt="Full size image"
                width={800}
                height={800}
                className="max-w-[90vw] max-h-[90vh] object-contain"
                unoptimized={true}
              />
            </div>
          )}

          {/* Method Button */}
          <div className="w-full md:w-1/2 lg:w-1/3">
            <div className="relative w-full animate-[float_6s_ease-in-out_infinite]">
              {/* Star Badge */}
              <div className="absolute -top-6 -right-6 w-20 h-20 z-10 animate-[spin_3s_linear_infinite]">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <path
                    d="M50 0 L61 35 L97 35 L68 57 L79 91 L50 70 L21 91 L32 57 L3 35 L39 35 Z"
                    fill="url(#starGradient)"
                    stroke="#ffd700"
                    strokeWidth="1"
                  />
                  <defs>
                    <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#ffd700' }} />
                      <stop offset="50%" style={{ stopColor: '#ffa700' }} />
                      <stop offset="100%" style={{ stopColor: '#ff8c00' }} />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white font-black text-sm" style={{ 
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                    fontFamily: 'Impact'
                  }}>FREE</span>
                </div>
              </div>

              <button 
                type="button"
                className="group relative inline-flex items-center justify-center w-full px-8 py-4 text-[1.2rem] text-white uppercase tracking-wider overflow-hidden rounded-[12px] transform hover:scale-[1.02] transition-all duration-200 font-serif"
                style={{
                  background: 'linear-gradient(to bottom, #ff4242 0%, #cc1f1f 49%, #991f1f 50%, #cc1f1f 100%)',
                  boxShadow: `
                    0 1px 0 rgba(255,255,255,0.6) inset,
                    0 -1px 0 rgba(0,0,0,0.3) inset,
                    0 0 0 1px rgba(255,255,255,0.2) inset,
                    0 20px 32px -8px rgba(0,0,0,0.5),
                    0 4px 8px rgba(0,0,0,0.3),
                    0 8px 16px rgba(0,0,0,0.3),
                    0 16px 32px rgba(0,0,0,0.3)
                  `,
                  border: '1px solid rgba(255,66,66,0.8)',
                  textShadow: '0 -2px 2px rgba(0,0,0,0.5)',
                  transform: 'perspective(1000px) rotateX(0deg)',
                  transformOrigin: 'center center',
                  transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  filter: 'contrast(1.1)'
                }}
                onClick={() => setShowMethodModal(true)}
                onMouseEnter={(e) => {
                  const btn = e.currentTarget;
                  btn.style.transform = 'perspective(1000px) rotateX(-12deg) translateY(2px)';
                  btn.style.filter = 'contrast(1.2)';
                }}
                onMouseLeave={(e) => {
                  const btn = e.currentTarget;
                  btn.style.transform = 'perspective(1000px) rotateX(0deg) translateY(0)';
                  btn.style.filter = 'contrast(1.1)';
                }}
              >
                {/* Primary Shine Layer */}
                <div 
                  className="absolute inset-0 overflow-hidden rounded-[12px] pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
                    transform: 'translateX(-100%) skewX(-25deg)',
                    animation: 'shine-slide 2s infinite',
                    zIndex: 2
                  }}
                />
                
                {/* Secondary Shine Layer */}
                <div 
                  className="absolute inset-0 overflow-hidden rounded-[12px] pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                    transform: 'translateX(-100%) skewX(-15deg)',
                    animation: 'shine-slide 2s infinite 0.3s',
                    zIndex: 2
                  }}
                />
                
                {/* Tertiary Shine Layer */}
                <div 
                  className="absolute inset-0 overflow-hidden rounded-[12px] pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transform: 'translateX(-100%) skewX(-35deg)',
                    animation: 'shine-slide 2s infinite 0.6s',
                    zIndex: 2
                  }}
                />
                
                {/* Permanent Shine Overlay */}
                <div 
                  className="absolute inset-0 rounded-[12px] pointer-events-none"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)',
                    opacity: 0.5,
                    zIndex: 1
                  }}
                />

                {/* Content Container */}
                <div className="relative flex items-center justify-center gap-3 whitespace-nowrap z-10">
                  <span className="animate-bounce">⚡️</span>
                  <span className="font-['Georgia'] tracking-wide" style={{ letterSpacing: '0.05em' }}>
                    DO YOU WANT THE METHOD??
                  </span>
                  <span className="animate-bounce">⚡️</span>
                </div>
              </button>

              {/* Stats Container */}
              <div className="mt-4 flex justify-center gap-4 text-[10px] font-mono">
                <div className="bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-[#33ff33]/20 transform hover:scale-105 transition-transform">
                  <span className="text-[#33ff33]">⚡️ SPEED:</span>
                  <span className="text-[#33ff33] ml-1 animate-pulse">44.2 MB/s</span>
                </div>
                <div className="bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-[#33ff33]/20 transform hover:scale-105 transition-transform">
                  <span className="text-[#33ff33]">📥 DLS:</span>
                  <span className="text-[#33ff33] ml-1">1.3M+</span>
                </div>
              </div>
            </div>
          </div>

        {/* Third Image */}
        <div className="w-full">
            <Image
              src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/loveiran.gif?v=1741147618"
              alt="Bottom Image"
              width={1920}
              height={1080}
              className="w-full"
              priority
              quality={100}
              unoptimized
            />
          </div>
        

        
          {/* FOURTH Image */}
          <div className="w-full md:w-1/2 lg:w-1/3">
            <div className="hero-image-container bg-gray-900">
              <Image 
                src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/97BF82C7-5514-4B69-A0F3-7258B222E483.jpg?v=1740199964"
                alt="Secondary Hero Image"
                className="hero-image"
                fill
                priority
                quality={100}
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              />
            </div>
          </div>

          


          
          
          {/* GIF Image - Full Width */}
          <div className="w-full animate-[oldSchoolBounce_0.15s_steps(2)_infinite]">
            <Image
              src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/fivestrokestratagem.gif?v=1740363447"
              alt="Bottom Image"
              width={1920}
              height={1080}
              className="w-full"
              priority
              unoptimized={true}
            />
          </div>


          {/* Pain Matrix XP - Flush with image above */}
          <div className="w-full">
            <div className="w-full h-[550px] md:h-[650px] lg:h-[700px] relative">
              <PainMatrixXPImproved className="w-full h-full" />
            </div>
          </div>

          {/* Video Container */}
          <div className="w-full md:w-1/2 lg:w-1/3">
            <div className="relative aspect-square bg-black">
              <video
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
              >
                <source src="https://cdn.shopify.com/videos/c/o/v/a701e0e40d74454484669fb3342f9842.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          {/* RAP Notice */}
          <div className="w-full md:w-1/2 lg:w-1/3">
            <div className="h-full bg-black/20 backdrop-blur-sm flex items-center">
              <div className="w-full text-center space-y-4 px-4 py-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-black/20 rounded-full">
                  <div className="w-2 h-2 rounded-full bg-[#33ff33] animate-pulse"></div>
                  <span className="text-[10px] font-mono text-[#33ff33]/80 tracking-wider">SYSTEM NOTICE</span>
                </div>
                
                <div className="font-mono text-white/80 text-sm">
                  RECOVERED ASSET PROGRAM [RAP] :: PHASE II
                </div>
                
                <Link 
                  href="/rap"
                  className="inline-flex items-center gap-2 group"
                >
                  <span className="text-xs font-mono text-[#666] group-hover:text-[#33ff33] transition-colors">
                    Learn more about asset recovery_
                  </span>
                  <div className="w-4 h-4 rounded-full bg-[#33ff33]/10 group-hover:bg-[#33ff33]/20 flex items-center justify-center transition-colors">
                    <span className="text-[10px] font-mono text-[#33ff33]">→</span>
                  </div>
                </Link>
              </div>
            </div>
          </div>

          {/* Keygen Image */}
          <div className="w-full md:w-1/2 lg:w-1/3">
            <div className="hero-image-container bg-gray-900">
              <Image 
                src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/df_keygen.jpg?v=1740356290"
                alt="Secondary Hero Image"
                className="hero-image"
                fill
                priority
                quality={100}
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              />
            </div>
          </div>
        </div>

        <div className="w-full flex justify-center">
          <div className="relative animate-[float_6s_ease-in-out_infinite] max-w-[1200px]">
            <Image
              src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/EOTM_MARCH.webp?v=1741363988"
              alt="Bottom Image"
              width={1920}
              height={1080}
              className="w-full"
              priority
              quality={100}
              unoptimized
            />
            <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 bg-[#33ff33]/5"></div>
          </div>
        </div>

        <div className="w-full">
          <Image
            src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/iandi.png?v=1740199011"
            alt="Bottom Image"
            width={1920}
            height={1080}
            className="w-full"
            priority
            quality={100}
            unoptimized
          />
        </div>

        {/* Video Container */}
        <div className="w-full md:w-1/2 lg:w-1/3">
          <div className="relative aspect-square bg-black">
            <OptimizedVideo
              src="https://cdn.shopify.com/videos/c/o/v/82d72a1fbc98491db12253d1ed760b78.mp4"
              autoPlay
              loop
              muted
              className="w-full h-full"
              objectFit="cover"
              preload="metadata"
            />
          </div>
        </div>

        
        {/* Video Container */}
        <div className="w-full md:w-1/2 lg:w-1/3">
          <div className="relative aspect-square bg-black">
            <video
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            >
              <source src="https://cdn.shopify.com/videos/c/o/v/f9250d0a0b8d4594839931543e85b7c3.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        <div className="w-full">
          <Image
            src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/unlockingtool.jpg?v=1741349346"
            alt="Bottom Image"
            width={1920}
            height={1080}
            className="w-full"
            priority
            quality={100}
            unoptimized
          />
        </div>

        {/* Content Divider Image */}
        <div className="w-full max-w-[800px] mx-auto px-4 py-8 flex justify-center">
          <OptimizedImage
            src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/divider_df.webp?v=1740701982"
            alt="Content Divider"
            width={800}
            height={2}
            className="opacity-50"
            imageType="decorative"
          />
        </div>
        
        {/* PGPENCRIP Image */}
        <div className="w-full">
          <OptimizedImage
            src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/pgpencrip_658ce6de-1ffd-4ffd-95bb-a0142871a05e.png?v=1741358258"
            alt="PGP Encryption"
            width={1920}
            height={1080}
            className="w-full"
            imageType="hero"
            quality={90}
          />
        </div>

        {/* PGP Key Container */}
        <div className="w-full max-w-[800px] mx-auto px-4 py-2">
          
          <div className="relative bg-[#1a1a1a] border border-[#444] rounded-md overflow-hidden shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between p-2 bg-[#2a2a2a] border-b border-[#444]">
              <div className="flex items-center gap-2">
                
                <span className="text-[#888]">🔑</span>
                <span className="text-[#888] font-mono text-sm">PGP Public Key</span>
              </div>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(`-----BEGIN PGP PUBLIC KEY BLOCK-----
Version: Keybase OpenPGP v1.0.0
Comment: https://keybase.io/crypto
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=G8/j
-----END PGP PUBLIC KEY BLOCK-----`);
                  alert('PGP key copied to clipboard!');
                }}
                className="flex items-center gap-1 px-2 py-1 bg-[#3a3a3a] hover:bg-[#444] rounded text-xs text-[#aaa] font-mono transition-colors"
              >
                <span>📋</span>
                Copy
              </button>
            </div>
            
            {/* Key Content - Scrollable */}
            <div className="max-h-[200px] overflow-y-auto p-3 font-mono text-xs">
              <pre className="text-[#ccc] whitespace-pre-wrap break-all leading-relaxed selection:bg-[#444] selection:text-white">
{`-----BEGIN PGP PUBLIC KEY BLOCK-----
Version: Keybase OpenPGP v1.0.0
Comment: https://keybase.io/crypto
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=G8/j
-----END PGP PUBLIC KEY BLOCK-----`}</pre>
            </div>
            
            {/* Footer */}
            <div className="flex justify-between items-center p-2 bg-[#2a2a2a] border-t border-[#444] text-xs font-mono">
              <div className="text-[#666]">ID: 5D76 4AA7 6DA0 3768</div>
              <div className="text-[#666]">VERIFIED ✓</div>
            </div>
          </div>
        </div>

        

        {/* Floating Tux Assistant */}
        <button 
          onClick={() => {
            setEmail('');
            setShowMenu(true);
          }}
          className="fixed bottom-8 right-8 z-50 group cursor-pointer"
        >
          <div className="relative">
            {/* Tux Container */}
            <div className="w-24 h-24 relative">
              <Image
                src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/tuxangel.gif"
                alt="Tux Assistant"
                fill
                className="object-contain"
                unoptimized
              />
            </div>
          </div>
        </button>

        {/* Newsletter Modal */}
        {showMenu && showNewsletterModal && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto"
               style={{
                 position: 'fixed',
                 height: '100vh',
                 overflowY: 'auto'
               }}>
            <div className="max-w-md w-full mx-auto relative rounded-md overflow-hidden my-8"
                 style={{
                   background: 'linear-gradient(to bottom, #2a2a2a 0%, #1a1a1a 100%)',
                   boxShadow: `
                     0 0 0 1px rgba(255,255,255,0.1),
                     0 8px 20px rgba(0,0,0,0.6)
                   `,
                   border: '1px solid rgba(255,255,255,0.1)'
                 }}>
              {/* Modal Header */}
              <div className="relative"
                   style={{
                     background: 'linear-gradient(to bottom, #4a4a4a 0%, #333333 50%, #2a2a2a 51%, #333333 100%)',
                     boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15)',
                     borderBottom: '1px solid #111'
                   }}>
                <div className="px-6 py-3 flex justify-between items-center">
                  <h2 className="text-lg font-bold text-white"
                      style={{
                        textShadow: '0 -1px 0 rgba(0,0,0,0.5)',
                        fontFamily: '"Trebuchet MS", Arial, sans-serif'
                      }}>
                    ACCESSS NEWSLETTER
                  </h2>
                  <button 
                    onClick={() => setShowMenu(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                    style={{
                      textShadow: '0 1px 0 rgba(0,0,0,0.5)'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a]">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="relative">
                    <input 
                      type="email" 
                      placeholder="Enter your email for classified intel"
                      className="w-full px-4 py-3 rounded text-white bg-[#0a0a0a] border border-[#333]/50 focus:border-[#66ff66]/50 focus:outline-none"
                      style={{
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3), 0 1px 0 rgba(255,255,255,0.1)'
                      }}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <button 
                    type="submit"
                    className="w-full relative overflow-hidden group"
                    disabled={loading}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-[#66ff66]/20 to-[#33ff33]/20 group-hover:from-[#66ff66]/30 group-hover:to-[#33ff33]/30 transition-colors"></div>
                    <div className="relative px-6 py-3 rounded"
                         style={{
                           background: 'linear-gradient(to bottom, #33ff33 0%, #2acc2a 50%, #1f991f 51%, #2acc2a 100%)',
                           boxShadow: `
                             inset 0 1px 0 rgba(255,255,255,0.4),
                             inset 0 -1px 0 rgba(0,0,0,0.3),
                             0 1px 0 rgba(0,0,0,0.3)
                           `,
                           border: '1px solid #1a661a'
                         }}>
                      <span className="block text-center font-bold text-white text-shadow-sm"
                            style={{
                              textShadow: '0 -1px 0 rgba(0,0,0,0.5)',
                              fontFamily: '"Trebuchet MS", Arial, sans-serif'
                            }}>
                        {loading ? 'CONNECTING...' : 'JOIN NETWORK'}
                      </span>
                    </div>
                  </button>
                </form>
                {error && (
                  <div className="mt-4 p-3 rounded bg-red-500/10 border border-red-500/20">
                    <p className="text-red-500 text-sm"
                       style={{
                         fontFamily: '"Lucida Console", Monaco, monospace'
                       }}>
                      [ERROR] {error}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-3 bg-[#1a1a1a] border-t border-[#333]/50 text-center">
                <p className="text-xs text-gray-500"
                   style={{
                     fontFamily: '"Lucida Console", Monaco, monospace'
                   }}>
                  © 2025 DONGFENG GROUP LLC
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
      
      {/* Add SpeedInsights component */}
      <SpeedInsights />
    </div>
  )
}